import { useSelector, useDispatch } from 'react-redux';
import * as action from '../actions/dataEvents';
import { bindActionCreators } from 'redux';
import { RootState } from '../reducers';
import { DataEvents, DataMeta, EventData } from '../../config/commonTypes';
import { useRouter } from '../../helpers/customHooks';
import { getLiveEntities, loadLiveDataBallMetricsPerformance, loadPastDataBallMetricsPerformance } from '../actions';
import { loadPracticeProcessingStatus } from '../actions/processingStatus';

type RouteParams = {
  params: { id: string, playerId: string }
}

type TeamOfInterest = {
  homeTeamId: string;
  awayTeamId: string;
}

export const useDataEventsState = () => {
  const { params } = useRouter<RouteParams>();
  const dataEvents = useSelector((state: RootState) => state.dataEvents) as DataEvents;
  const { id } = params;
  return { dataEvents, dataEvent: dataEvents[id], ...dataEvents[id] };
};

export const useDataEventsDispatch = () => {
  const { params } = useRouter<RouteParams>();
  const dispatch = useDispatch();

  const { id, playerId } = params;
  return {
    ...bindActionCreators({ ...action }, dispatch),
    loadDataMetaGame: () => {
      dispatch(action.loadDataMetaGame(id));
    },
    cleanPlayersSession: () =>{
      dispatch(action.cleanPlayersSession(id));
    },
    loadPracticePlayers: () => {
      dispatch(action.loadPracticePlayers(id));
    },
    loadDataProcessingStatus: (endTime?:number, teamId?:string, fullStatus?:boolean) => {
      dispatch(loadPracticeProcessingStatus(id, endTime, teamId, fullStatus));
    },
    cleanPracticeViewerData: () => {
      dispatch(action.cleanPracticeViewerData(id));
    },
    loadDataEventsOnLocalStorage: () => {
      dispatch(action.loadDataEventsOnLocalStorage(id));
    },
    getDataLivePractice: async (teamId: string, listOfPlayersExcluded?: string, isMobile?: boolean) => {
      if(!isMobile)await dispatch(getLiveEntities(id));
      await dispatch(action.loadLiveDataPerformance(id, true, teamId, listOfPlayersExcluded, isMobile));
    },
    getDataPastEventData: async (
      teamId: string,
      withLoader: boolean,
      teamOfInterest?: TeamOfInterest,
      awayTeam?: boolean,
      listOfPlayersExcluded?: string,
      isMobile?: boolean
    ) => {
      await dispatch(action.loadPastDataEventPerformance(id, teamId, withLoader, teamOfInterest, awayTeam, listOfPlayersExcluded, isMobile)); // gameId, teamId
    },
    getLiveData: (loading: boolean, teamId: string, listOfPlayers?: string, isMobile?: boolean, liveMonitor?: boolean) => {
      dispatch(action.loadLiveDataPerformance(id, loading, teamId, listOfPlayers, isMobile, liveMonitor));
    },
    getScheduleData: async (isMobile?:boolean) => {
      await dispatch(action.loadSchedulePractice(id, isMobile));
    },
    loadPlayersSession: (startTime: number, teamId: string) => {
      dispatch(action.loadPlayersSession(id, startTime, teamId));
    },
    getTeamPracticeFields: (teamId: string) => {
      dispatch(action.getTeamPracticeFields(teamId, id));
    },
    livePracticePlayers: (startTime: number) => {
      dispatch(action.livePracticePlayers(startTime, id));
    },
    editPracticeTime: (practice: DataMeta, eventId: string, league: string) => {
      dispatch(action.editPracticeStartTime(practice, id, eventId, league, true));
    },
    getPastPracticeModalData: (teamId: string) => {
      dispatch(action.loadPlayerModal(id, playerId, teamId)); // gameId, playerId, teamId
      dispatch(action.getVelocityGraphData(id, playerId, false));
    },
    getLivePracticeModalData: (teamId: string, loader: boolean) => {
      dispatch(action.loadPlayerModalLive(id, playerId, teamId, loader));
      dispatch(action.getVelocityGraphData(id, playerId, true));
    },
    getBallMetricsPracticeModalData: (loader: boolean, live: boolean) => {
      dispatch(action.loadBallMetricsModal(id, playerId, loader, live)); // gameId, playerId, teamId
    },
    getBallMetricsPastData: (teamId?: string, isMobile?:boolean) => () =>  { // only for practices no games
      dispatch(loadPastDataBallMetricsPerformance(id, true, teamId, isMobile));
    },
    getBallMetricsLiveData: (priority: boolean, isMobile?:boolean) => () => { // only for practices no games
      dispatch(loadLiveDataBallMetricsPerformance(id, priority, isMobile));
    }
  };
};

export const usePracticeViewerState = (
  teamOfInterest?: string | number
) => {
  const { dataEvent } = useDataEventsState();


  const dataKey = teamOfInterest ? `data:${teamOfInterest}` : 'data';

  const dataByTeam = dataEvent[dataKey] as EventData ;

  return {
    practiceData: dataEvent.meta,
    ...(dataByTeam?.practiceViewer ?? {})
  };
};